/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prettier/prettier */
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { Toolbar } from "@mui/material";
import AppModalDialogs from "./components/AppModalDialogsV2";
import ImagePreloader from "./components/ImagePreloader";
import PageFooter from "./components/PageFooter";
import AppNavigation from "components/AppNavigation";
import { useEnvContext, useWebStoreContext } from "contexts";
import { useDispatch } from "react-redux";
import { offersApi } from "redux/apis";
import { useRedirectFTUE, useServiceWorkerVersionReload } from "hooks";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { WsDialogs as IDialogVariants } from "interfaces/dialog/variant";
import { useLoadSignUpIncentive } from "hooks/useLoadSignUpIncentive";
import useDeeplinkAction from "hooks/useDeeplinkAction";
import { useInAppDipToWebDip } from "hooks/useInAppDipToWebDip";
import useLoadOffers from "hooks/useLoadOffers";
import { useNetworkLogsObserver } from "debug/components/NetworkLogs/helpers/networkLogsObserver";
import { useRebateFocus } from "components/RebateOffer/hooks/useRebateFocus";
import { useDripOffer } from "pages/DripOffer/hooks/useDripOffer";
import { useClubGiftGalore } from "pages/ClubShop/ClubEventSectionV2/hook/useClubGiftGalore";
import useSaleTagsPolling from "components/StickySaleTag/hooks/useSaleTagsPolling";
import { AppRoutes } from "./routes/AppRoutes";
import { usePushNotificationClickAnalytics } from "components/PWA/hooks/usePushNotificationClickAnalytics";
import { usePWATriggerReward } from "components/PWA/hooks/usePWATriggerReward";
import { usePushTriggerPermissions } from "components/PWA/hooks/usePushTriggerPermissions";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import useFriendInvite from "pages/FriendCenter/FriendInvite/hooks";
import useLoadPurchaseBanned from "components/Chargeback/hooks/useLoadPurchaseBanned";

const MainContent = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { refetchFeatureFlags } = useEnvContext();
	const [params, setParams] = useState<{ [key: string]: string }>();
	const { userTracker, immediateLogin, authToken } = useWebStoreContext();
	const isFooterEnabled = location.pathname !== "/privacy-ads-toggle";
	const { variant, dialogState, closeDialog } = useDialogModalRedux();

	const [suppressPopups] = useState<boolean>(
		searchParams.get("focus") === "rebate"
	);
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	useEffect(() => {
		refetchFeatureFlags();
	}, [authToken?.value, refetchFeatureFlags]);

	useLoadOffers();
	useFriendInvite();
	useDripOffer({ suppressPopups });
	useDeeplinkAction();
	useClubGiftGalore({ suppressPopups });
	useLoadSignUpIncentive();
	useInAppDipToWebDip();
	useRebateFocus();
	useNetworkLogsObserver();
	useSaleTagsPolling();
	useRedirectFTUE();
	usePWATriggerReward();
	usePushTriggerPermissions();
	usePushNotificationClickAnalytics();
	useServiceWorkerVersionReload();
	useLoadPurchaseBanned();

	useEffect(() => {
		// New analytics: trigger when the webstore successfully shows up in the app
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "webstore_session_start",
			event_type: "loading",
			action: "app_loading_success",
			source: "loading",
			link_url: window.location.href
		});
	}, []);

	useEffect(() => {
		const params: { [key: string]: string } = {};
		for (const entry of searchParams.entries()) {
			params[entry[0]] = entry[1];
		}
		Object.entries(params).length > 0 && setParams(params);
	}, []);

	useEffect(() => {
		if (params) {
			if (params.token && params.loginType === "auto") {
				immediateLogin(params.token);
			} else {
				const paramsString = new URLSearchParams(params).toString();
				navigate(`${location.pathname}?${paramsString}`);
			}
		}
	}, [params]);

	// Invalidates site offers if clubId changes
	useEffect(() => {
		dispatch(offersApi.util.invalidateTags(["offersTag"]));
	}, [userTracker?.clubId]);

	useEffect(() => {
		if (
			location.pathname === "/daily-gift" ||
			location.pathname === "/daily-free-gift"
		) {
			navigate("/free-daily-gifts");
		}
	}, [location]);

	useEffect(() => {
		if (
			location.pathname !== "/shop" &&
			variant === IDialogVariants.LOGIN_TO_CONNECT &&
			dialogState?.source === "shop"
		) {
			closeDialog();
		}
	}, [location]);

	useEffect(() => {
		console.log(
			"PWA VAPID PUBLIC KEY",
			process.env.REACT_APP_PUSH_VAPID_PUBLIC_KEY
		);
	}, []);

	return (
		<Fragment>
			<Fragment>
				<Toolbar sx={{ minHeight: { xs: 55, sm: 64 }, padding: "0 12px" }} />
				<AppNavigation>
					<AppRoutes />
				</AppNavigation>
			</Fragment>

			<AppModalDialogs />
			<ImagePreloader />
			{isFooterEnabled && <PageFooter />}
		</Fragment>
	);
};

export default function MainComponent() {
	return <MainContent />;
}
